import React, { useState, useEffect } from 'react';
import { API_URL, secret_key, storedData } from '../Constants';
import axios from 'axios';
import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import loadingGif from "../../assets/loading.gif";
import './Dashboard.css';
import { formatCurrency } from "../../utils/currency";

const Dashboard = () => {
  const navigate = useNavigate();
  // --- Decrypt User Token and Data ---
  const encryptedToken = localStorage.getItem('token');
  let decryptedToken = '';
  if (encryptedToken) {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedToken, secret_key);
      decryptedToken = bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error('Token decryption error:', error);
    }
  }
  // --- Decrypt user info ---
  let loggedUserData = null;
  if (storedData) {
    try {
      const bytes = CryptoJS.AES.decrypt(storedData, secret_key);
      loggedUserData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
      console.error("User data decryption error:", err);
    }
  }

  // --- State Declarations ---
  const [credits, setCredits] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [events, setEvents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [assignments, setAssignments] = useState([]);
  
  const [companyData, setCompanyData] = useState({
    api_key: "",
    city_id: "",
    city_name: "",
    created_at: "",
  });


  const [isSubmitting, setIsSubmitting] = useState(false);



// --- API Fetch Function for Student Fees ---
useEffect(() => {
  if (!loggedUserData) return; // Exit if user data not ready

  const fetchDashboardData = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`,
        {
          headers: { Authorization: `CustomToken ${secret_key}` },
        }
      );

      if (!res.data.success) {
        console.warn("API returned failure:", res.data.message);
        return;
      }

      const { dashboard, details, hot_notifications, events } = res.data;

      // Dashboard summary
      setCompanyData({
        balance_bf: dashboard.balance_bf || 0,
        invoiced: dashboard.invoiced || 0,
        discounted: dashboard.discount || 0,
        paid: dashboard.paid || 0,
        balance: dashboard.balance || 0,
        details: details || {},
      });

      // Student details
      const studentDetails = Object.entries(details || {}).map(([admNo, data]) => ({
        admission_no: admNo,
        balance_bf: data.balance_bf || 0,
        invoiced: data.invoiced || 0,
        discount: data.discount || 0,
        paid: data.paid || 0,
        balance: data.balance || 0,
        recent_payments: Array.isArray(data.recent_payments) ? data.recent_payments : [],
      }));
      setCredits(studentDetails);

      // Notifications & events
      setNotifications(hot_notifications || []);
      setEvents(events || []);
    } catch (err) {
      console.error("Dashboard data fetch error:", err);
    }
  };

  fetchDashboardData();
}, [loggedUserData]); // ✅ Only runs once loggedUserData is ready


useEffect(() => {
  if (!loggedUserData) return;

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`, 
          {
          headers: {
          Authorization: `CustomToken ${secret_key}`,   // 🔐 Use passed key
          },
          }
    );

      if (res.data.success) {
        // Save the hot notifications here
        setNotifications(res.data.hot_notifications || []);
      }

    } catch (err) {
      console.error("Notifications fetch error:", err);
    }
  };

fetchNotifications();
}, [loggedUserData]);

useEffect(() => {
  if (!loggedUserData) return;

  const fetchAttendance = async () => {
    try {
      const res = await axios.get(`${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`, 
          {
          headers: {
          Authorization: `CustomToken ${secret_key}`,   // 🔐 Use passed key
          },
          }
    );

      if (res.data.success) {
        // Save the hot notifications here
        setAttendance(res.data.attendance_register_details || []);
      }

    } catch (err) {
      console.error("Attendance fetch error:", err);
    }
  };

  fetchAttendance();
}, [loggedUserData]);
/* Assignments */
 useEffect(() => {
  if (!loggedUserData) return;

  const fetchAssignments = async () => {
    try {
      const res = await axios.get(`${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`, 
          {
          headers: {
          Authorization: `CustomToken ${secret_key}`,   // 🔐 Use passed key
          },
          }
    );

      if (res.data.success) {
        // Save the hot notifications here
        setAssignments(res.data.assignments || []);
      }

    } catch (err) {
      console.error("Assignments fetch error:", err);
    }
  };

  fetchAssignments();
}, [loggedUserData]);
/* Assignments END */

useEffect(() => {
  if (!loggedUserData) return;

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/StudentFeesRecords/${loggedUserData.company_id}/${loggedUserData.parent_no}`, 
      {
        headers: {
          Authorization: `CustomToken ${secret_key}`,   // 🔐 Use passed key
        },
      }
    );

      if (res.data.success) {
        // Save the hot notifications here
        setEvents(res.data.events || []);
      }

    } catch (err) {
      console.error("Events fetch error:", err);
    }
  };

  fetchEvents();
}, [loggedUserData]);



  // --- JSX Rendering ---
return (
  <div className="dashboard-container container-fluid position-relative">

    {/* Loading Overlay */}
    {isSubmitting && (
      <div className="loading-overlay d-flex align-items-center justify-content-center">
        <img className="loading-img" src={loadingGif} alt="Loading..." />
      </div>
    )}

    {/* HEADER */}
    <header className="dashboard-header py-3 border-bottom mb-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
        <h1 className="mb-0 fw-bold">Dashboard</h1>

        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item active" aria-current="page">Dashboard</li>
          </ol>
        </nav>
      </div>
    </header>

    <div className="row gy-4">

      {/* LEFT MAIN COLUMN */}
      <div className="col-12 col-lg-8">

        {/* ===== FEES SUMMARY CARDS ===== */}
        <div className="row gy-4 mb-3">
          {[
            { label: 'B/f', value: companyData.balance_bf || '0.00', icon: 'fas fa-wallet', gradient: 'bg-gradient-primary' },
            { label: 'Invoiced', value: companyData.invoiced || '0.00', icon: 'fas fa-file-invoice-dollar', gradient: 'bg-gradient-info' },
            { label: 'Discounted', value: companyData.discount || '0.00', icon: 'fas fa-percentage', gradient: 'bg-gradient-danger' },
            { label: 'Paid', value: companyData.paid || '0.00', icon: 'fas fa-money-check-alt', gradient: 'bg-gradient-success' },
            { label: 'Balance', value: companyData.balance || '0.00', icon: 'fas fa-wallet', gradient: 'bg-gradient-warning' }
          ].map((card, idx) => (
            <div className="col-6 col-md-3" key={idx}>
              <div className={`card text-white shadow-sm border-0 rounded-4 ${card.gradient}`}>
                <div className="card-body d-flex align-items-center justify-content-between">
                  <div className="me-3 icon-circle bg-white text-dark shadow-sm p-3 rounded-circle">
                    <i className={`${card.icon} fa-lg`}></i>
                  </div>
                  <div className="text-end">
                    <h6 className="text-uppercase fw-bold mb-1 small">{card.label}</h6>
                    <h4 className="fw-bold mb-0">{card.value}</h4>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ===== STUDENT FEES DETAILS TABLE ===== */}
        <div className="card shadow-sm rounded-4 mb-4">
          <div className="card-header bg-gradient-secondary text-white rounded-top-4">
            <h5 className="mb-0">
              <i className="fas fa-list me-2"></i>Student Fees Details
            </h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive d-none d-md-block">
              <table className="table table-striped mb-0 align-middle w-100">
                <thead>
                  <tr>
                    <th>Student No</th>
                    <th>Name</th>
                    <th>B/F</th>
                    <th>Invoiced</th>
                    <th>Discount</th>
                    <th>Paid</th>
                    <th>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(companyData.details || {}).map(([studentNo, data], idx) => (
                    <tr key={idx}>
                      <td>{data.student_no}</td>
                      <td className="text-wrap">{data.full_name}</td>
                      <td>{data.balance_bf}</td>
                      <td>{data.invoiced}</td>
                      <td>{data.discount}</td>
                      <td>{data.paid}</td>
                      <td>{data.balance}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
          {/* ===== MOBILE VIEW (CARDS) ===== */}
<div className="d-md-none">

{Object.entries(companyData.details || {}).map(([studentNo, data], idx) => (

<div className="student-fee-card mb-3" key={idx}>

<div className="student-fee-header">
<strong>{data.full_name}</strong>
<span className="badge bg-primary">{data.student_no}</span>
</div>

<div className="student-fee-body">

<div className="fee-row">
<span>B/F</span>
<strong>{data.balance_bf}</strong>
</div>

<div className="fee-row">
<span>Invoiced</span>
<strong>{data.invoiced}</strong>
</div>

<div className="fee-row">
<span>Discount</span>
<strong>{data.discount}</strong>
</div>

<div className="fee-row">
<span>Paid</span>
<strong className="text-success">{data.paid}</strong>
</div>

<div className="fee-row">
<span>Balance</span>
<strong className="text-danger">{data.balance}</strong>
</div>

</div>

</div>

))}

</div>
        {/* ===== RECENT PAYMENTS ===== */}
        <div className="card shadow-sm rounded-4 mb-4">
          <div className="card-header bg-gradient-success text-white rounded-top-4">
            <h5 className="mb-0">
              <i className="fas fa-chart-pie me-2"></i>Recent Payments
            </h5>
          </div>

          <div className="card-body p-0">
            <div className="table-responsive d-none d-md-block">
              <table className="table table-striped mb-0 align-middle w-100">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Document No</th>
                    <th>Admission No</th>
                    <th>Name</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {credits.flatMap(student =>
                    (student.recent_payments || []).map((payment, idx) => (
                      <tr key={`${student.admission_no}-${idx}`}>
                        <td>{payment.date}</td>
                        <td>{payment.document_no}</td>
                        <td>{payment.reference}</td>
                        <td className="text-wrap">{payment.student_name}</td>
                        <td>{payment.amount}</td>
                      </tr>
                    ))
                  )}

                  {/* Totals */}
                  <tr className="fw-bold bg-light">
                    <td colSpan="4">Totals</td>
                    <td>
                      {credits
                        .flatMap(student => student.recent_payments || [])
                        .reduce((sum, p) => {
                          const amt = parseFloat(String(p.amount || "0").replace(/,/g, ""));
                          return sum + amt;
                        }, 0)
                        .toLocaleString("en-KE", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
          {/* Payments Mobile View */}
          {/* ===== MOBILE PAYMENTS VIEW ===== */}
          <div className="d-md-none p-3">

          {credits.flatMap(student =>
            (student.recent_payments || []).map((payment, idx) => (

          <div className="payment-card mb-3" key={`${student.admission_no}-${idx}`}>

          <div className="payment-header">
          <strong>{payment.student_name}</strong>
          <span className="badge bg-primary">{payment.reference}</span>
          </div>

          <div className="payment-body">

          <div className="payment-row">
          <span>Date</span>
          <strong>{payment.date}</strong>
          </div>

          <div className="payment-row">
          <span>Document No</span>
          <strong>{payment.document_no}</strong>
          </div>

          <div className="payment-row">
          <span>Amount</span>
          <strong className="text-success">
               {formatCurrency(payment.amount)}
          </strong>
          </div>

          </div>

          </div>

          ))
          )}

          {/* TOTALS CARD */}

          <div className="payment-total-card">

          <div className="d-flex justify-content-between align-items-center">

          <strong>Total Payments</strong>

          <strong className="text-success">
          {credits
          .flatMap(student => student.recent_payments || [])
          .reduce((sum,p)=>{
          const amt=parseFloat(String(p.amount || "0").replace(/,/g,""))
          return sum+amt
          },0)
          .toLocaleString("en-KE",{
          minimumFractionDigits:2,
          maximumFractionDigits:2
          })}
          </strong>

          </div>

          </div>

          </div>

      </div>

      {/* ===== RIGHT COLUMN (NOTIFICATIONS + SEND SMS) ===== */}
      <div className="col-12 col-lg-4">

        {/* HOT NOTIFICATIONS */}
        <div className="card shadow-lg mb-4 border-0 rounded-4 sticky-top" style={{ top: "80px" }}>
          <div className="card-header bg-gradient-danger text-white rounded-top-4 d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><i className="fas fa-bell me-2"></i>Hot Notifications</h5>
          </div>

          <div className="card-body p-2">

            {/* Table header */}
            <div className="d-flex fw-bold border-bottom py-2 text-muted small">
              <div style={{ width: "40px" }}>SN</div>
              <div className="flex-grow-1">Title</div>
              <div className="text-end">SMS</div>
            </div>

            {/* Table body */}
            {notifications.length > 0 ? (
              notifications.slice(0, 5).map((note, idx) => (
                <div key={idx} className="d-flex align-items-center border-bottom py-2 position-relative">
                  <span className="flash-accent"></span>

                  <div style={{ width: "40px" }} className="fw-bold text-secondary">
                    {idx + 1}
                  </div>

                  <div className="flex-grow-1">
                    <div className="fw-semibold text-dark">{note.title}</div>
                    <small className="text-muted">{new Date(note.date_created).toLocaleString()}</small>
                  </div>

                  <div className="text-end text-primary fw-bold">
                    {note.sms}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-muted small text-center mb-0 py-3">No new notifications</p>
            )}
          </div>
        </div>
      </div>
{/* ===== Attendance Register Info ===== */}
<div className="col-12 col-lg-8">
  <div 
    className="card shadow-lg mb-4 border-0 rounded-4 sticky-top" 
    style={{ top: "80px" }}
  >

    {/* Header */}
    <div className="card-header bg-info text-white rounded-top-4">
      <h5 className="mb-0 d-flex align-items-center">
        <i className="fas fa-clipboard-list me-2"></i>
        Attendance Register Info
      </h5>
    </div>

    <div className="card-body p-3">

      {/* Table Header */}
      <div className="row fw-bold text-muted small border-bottom pb-2 mb-2 text-center">
        <div className="col-1">#</div>
        <div className="col-3 text-start">Date</div>
        <div className="col-2">Adm</div>
        <div className="col-3 text-start">Name</div>
        <div className="col-1">Morning</div>
        <div className="col-1">Evening</div>
      </div>

      {/* Table Body */}
      {attendance.length > 0 ? (
        attendance.slice(0, 5).map((attend, idx) => {

          const morningPresent = attend.morning_status === "1";
          const eveningPresent = attend.evening_status === "1";

          return (
            <div
              key={idx}
              className="row align-items-center small py-2 border-bottom text-center attendance-row"
            >
              <div className="col-1 fw-bold text-secondary">
                {idx + 1}
              </div>

              <div className="col-3 text-start">
                <div className="fw-semibold">{attend.document_date}</div>
              </div>

              <div className="col-2">
                {attend.admin_no}
              </div>

              <div className="col-3 text-start text-truncate">
                {attend.full_name}
              </div>

              {/* Morning */}
              <div className="col-1">
                <span className={`status-pill ${
                  morningPresent ? "status-present" : "status-absent"
                }`}>
                  <i className={`fas ${
                    morningPresent ? "fa-check-circle" : "fa-times-circle"
                  } me-1`}></i>
                  {morningPresent ? "Present" : "Absent"}
                </span>
              </div>

              {/* Evening */}
              <div className="col-1">
                <span className={`status-pill ${
                  eveningPresent ? "status-present" : "status-absent"
                }`}>
                  <i className={`fas ${
                    eveningPresent ? "fa-check-circle" : "fa-times-circle"
                  } me-1`}></i>
                  {eveningPresent ? "Present" : "Absent"}
                </span>
              </div>
            </div>
          );
        })
      ) : (
        <p className="text-muted small text-center mb-0 py-3">
          No new attendance records
        </p>
      )}

    </div>
  </div>
</div>
{/* ===== Recent Assignments ===== */}
<div className="col-12 col-lg-8">
  <div className="card shadow-lg border-0 rounded-4 mb-4 assignment-container">

    {/* Header */}
    <div className="card-header assignment-header rounded-top-4">
      <h5 className="mb-0 d-flex align-items-center">
        <i className="fas fa-book-open me-2"></i>
        Recent Assignments
      </h5>
    </div>

    <div className="card-body p-4">

      {assignments.length > 0 ? (
        assignments.slice(0,5).map((a, idx) => {

          const dueDate = new Date(a.due_date);
          const today = new Date();
          const isOverdue = dueDate < today;

          return (

            <div
              key={idx}
              className="assignment-panel mb-4"
            >

              {/* Top Meta */}
              <div className="assignment-meta">

                <div className="assignment-tags">

                  <span className="badge class-badge">
                    {a.class_code}
                  </span>

                  <span className="badge subject-badge">
                    {a.course_code}
                  </span>

                </div>

                <div
                  className={`due-badge ${
                    isOverdue ? "due-overdue" : "due-active"
                  }`}
                >
                  <i
                    className={`fas ${
                      isOverdue ? "fa-exclamation-circle" : "fa-clock"
                    }`}
                  ></i>

                  {isOverdue ? "Overdue" : "Due"} : {a.due_date}

                </div>

              </div>


              {/* Title */}
              <h5 className="assignment-title">
                {a.title}
              </h5>


              {/* Instructions */}
              <div
                className="assignment-instructions"
                dangerouslySetInnerHTML={{ __html: a.instructions }}
              />


              {/* Footer */}
              <div className="assignment-footer">

                <div className="marks-box">
                  <i className="fas fa-star"></i>
                  Marks
                  <span>{a.marks}</span>
                </div>

              </div>

            </div>

          );

        })
      ) : (

        <div className="assignment-empty">
          <i className="fas fa-book-open"></i>
          <p>No assignments available</p>
        </div>

      )}

    </div>
  </div>
</div>
      {/* ===== UPCOMING EVENTS (FULL WIDTH ON MOBILE, 8-cols ON LARGE) ===== */}
      <div className="col-12 col-lg-8">
        <div className="card shadow-lg border-0 rounded-4 mb-4">
          <div className="card-header bg-primary text-white rounded-top-4 d-flex justify-content-between align-items-center">
            <h5 className="mb-0"><i className="fas fa-calendar-alt me-2"></i>Upcoming Events</h5>
          </div>

          <div className="card-body p-4">
            {events.length > 0 ? (
              events.slice(0, 5).map((ev, idx) => (
                <div key={idx} className="event-timeline-item mb-4 pb-3 border-bottom position-relative">

                  <span className="event-dot"></span>

                  <h5 className="fw-bold mb-1">{ev.event_name}</h5>

                  <p className="text-muted small mb-2">
                    <i className="fas fa-map-marker-alt me-1 text-danger"></i>
                    {ev.location}
                  </p>

                  <div className="d-flex justify-content-between flex-wrap gap-2">
                    <span className="text-primary fw-semibold">
                      <i className="fas fa-calendar-day me-1"></i>
                      {new Date(ev.start_date).toLocaleDateString()}
                    </span>

                    <span className="text-secondary fw-semibold">
                      <i className="far fa-clock me-1"></i>
                      {ev.start_time} – {ev.end_time}
                    </span>
                  </div>

                </div>
              ))
            ) : (
              <p className="text-muted small text-center py-2">No scheduled events</p>
            )}
          </div>
        </div>
      </div>

    </div>
  </div>
);


  
};

export default Dashboard;
